import * as THREE from "three";
import { Kit, instance, once, revolve, type V3 } from "./kit";

/** Shared vehicle parts: wheels (static ones merge into a kit; spinning ones are their own objects). */

interface WheelGeo { tyre: THREE.BufferGeometry; hub: THREE.BufferGeometry; back: THREE.BufferGeometry; cap?: THREE.BufferGeometry; }

/** Axle along z, hub face at +z. `low` = 10–12 sided trucks' wheels (~80 tris); `high` = rounded tyre + dished rim. */
function wheelGeo(R: number, W: number, seg: number, detail: "low" | "high"): WheelGeo {
  return once(`g:wheel:${R}:${W}:${seg}:${detail}`, () => {
    if (detail === "low") {
      return {
        hub: revolve([[0, 0.3 * W], [0.62 * R, 0.3 * W]], seg),
        tyre: revolve([[0.62 * R, 0.3 * W], [R, 0.44 * W], [R, -0.44 * W], [0.62 * R, -0.5 * W]], seg),
        back: revolve([[0.62 * R, -0.5 * W], [0, -0.5 * W]], seg)
      };
    }
    return {
      cap: revolve([[0, 0.44 * W], [0.16 * R, 0.4 * W], [0.22 * R, 0.33 * W]], seg),
      hub: revolve([[0.22 * R, 0.33 * W], [0.55 * R, 0.27 * W], [0.62 * R, 0.36 * W], [0.66 * R, 0.33 * W]], seg),
      tyre: revolve([[0.66 * R, 0.33 * W], [0.9 * R, 0.5 * W], [R, 0.3 * W], [R, -0.3 * W], [0.9 * R, -0.5 * W], [0.66 * R, -0.42 * W]], seg, { smooth: true }),
      back: revolve([[0.66 * R, -0.42 * W], [0, -0.42 * W]], seg)
    };
  });
}

/** A static wheel merged into `kit`, centred at `at`, its hub facing +x (`side` = 1) or −x (−1). */
export function addWheel(kit: Kit, at: V3, R: number, W: number, side: 1 | -1, opts: { seg?: number; hub?: number; tyre?: number } = {}): void {
  const g = wheelGeo(R, W, opts.seg ?? 10, "low");
  const r: V3 = [0, (side * Math.PI) / 2, 0];
  kit.add(g.tyre, "rubber", opts.tyre ?? 0x1f2026, { p: at, r });
  kit.add(g.hub, "metal", opts.hub ?? 0xc4cad6, { p: at, r });
  kit.add(g.back, "rubber", 0x17171c, { p: at, r });
}

/**
 * A spinning wheel object: a Group named `name` at its hub centre, axle along its local x, so `rotation.x` rolls it
 * (hub on +x for `side` = 1). Shared geometry; ~290 tris.
 */
export function wheelObject(name: string, R: number, W: number, side: 1 | -1, rim = 0xd9dee8): THREE.Group {
  const wheel = instance(`wheel:${R}:${W}:${side}:${rim}`, () => {
    const g = wheelGeo(R, W, 18, "high");
    const kit = new Kit();
    const r: V3 = [0, (side * Math.PI) / 2, 0];
    kit.add(g.tyre, "rubber", 0x1d1e23, { r });
    kit.add(g.hub, "chrome", rim, { r });
    if (g.cap) kit.add(g.cap, "chrome", 0xf2f4f8, { r });
    kit.add(g.back, "rubber", 0x141418, { r });
    return kit.build();
  });
  wheel.name = name;
  return wheel;
}
